import { useState, forwardRef } from 'react'
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { DatePicker } from './DatePicker'

interface FloatingInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
}

interface FloatingTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  error?: string
}

interface FloatingSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  error?: string
  options: { value: string; label: string }[]
}

interface FloatingDateInputProps {
  label: string
  value: string
  min?: string
  onChange: (val: string) => void
  error?: string
}

function labelClass(floating: boolean, focused: boolean, error?: string) {
  return cn(
    'absolute left-3 transition-all duration-150 pointer-events-none',
    floating
      ? cn(
          'top-0 -translate-y-1/2 text-xs bg-white px-1',
          focused ? 'text-blue-500' : error ? 'text-red-400' : 'text-gray-400'
        )
      : 'top-3 text-sm text-gray-400'
  )
}

function inputBorderClass(focused: boolean, error?: string) {
  if (focused) return 'border-blue-500'
  if (error) return 'border-red-300'
  return 'border-gray-300'
}

export const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, error, className, onFocus, onBlur, value, ...props }, ref) => {
    const [focused, setFocused] = useState(false)
    const floating = focused || (!!value && String(value).length > 0)

    return (
      <div className="relative">
        <input
          ref={ref}
          value={value}
          {...props}
          placeholder=""
          onFocus={e => { setFocused(true); onFocus?.(e) }}
          onBlur={e => { setFocused(false); onBlur?.(e) }}
          className={cn(
            'w-full border rounded-xl px-3 py-3 text-sm focus:outline-none transition-colors placeholder:text-gray-300',
            inputBorderClass(focused, error),
            className
          )}
        />
        <label className={labelClass(floating, focused, error)}>{label}</label>
        {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
      </div>
    )
  }
)

export const FloatingTextarea = forwardRef<HTMLTextAreaElement, FloatingTextareaProps>(
  ({ label, error, className, onFocus, onBlur, value, ...props }, ref) => {
    const [focused, setFocused] = useState(false)
    const floating = focused || (!!value && String(value).length > 0)

    return (
      <div className="relative">
        <textarea
          ref={ref}
          value={value}
          {...props}
          placeholder=""
          onFocus={e => { setFocused(true); onFocus?.(e) }}
          onBlur={e => { setFocused(false); onBlur?.(e) }}
          className={cn(
            'w-full border rounded-xl px-3 py-3 text-sm focus:outline-none transition-colors resize-none',
            inputBorderClass(focused, error),
            className
          )}
        />
        <label className={labelClass(floating, focused, error)}>{label}</label>
        {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
      </div>
    )
  }
)

// Custom date picker with floating label
export function FloatingDateInput({ label, value, min, onChange, error }: FloatingDateInputProps) {
  const floating = !!value

  return (
    <div className="relative">
      <DatePicker value={value} min={min} onChange={onChange} />
      <label className={labelClass(floating, false, error)}>
        {label}
      </label>
      {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
    </div>
  )
}

export function FloatingSelect({ label, error, options, className, onFocus, onBlur, value, ...props }: FloatingSelectProps) {
  const [focused, setFocused] = useState(false)
  const floating = focused || (!!value && String(value).length > 0)

  return (
    <div className="relative">
      <select
        value={value}
        {...props}
        onFocus={e => { setFocused(true); onFocus?.(e) }}
        onBlur={e => { setFocused(false); onBlur?.(e) }}
        className={cn(
          'w-full border rounded-xl px-3 py-3 text-sm focus:outline-none transition-colors bg-white appearance-none',
          inputBorderClass(focused, error),
          !value && 'text-transparent',
          className
        )}
      >
        <option value="" disabled />
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <label className={labelClass(floating, focused, error)}>{label}</label>
      {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
    </div>
  )
}
